import PropTypes from 'prop-types';
import { HSRegistry } from '@/schemas/registry/HSRegistry.js';
import { evalExpression } from '@/schemas/utils/expression.js';

/**
 * Renders UI components dynamically based on a schema definition.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.schema - The schema object describing the UI to render. Should have a `fields` array, where each field is an object with:
 *   - `x-component` (string): The name of the component to render, as registered in HSRegistry.
 *   - `x-visible` (optional, string|boolean): An expression or boolean controlling visibility.
 *   - `x-props` (optional, object): Props to pass to the component, values can be expressions.
 *   - `x-meta` (optional, object): Additional metadata for the component.
 *   - `bind` (optional, string): The key in `data` to bind as the component's value.
 * @param {any} [props.data] - The data object used for binding field values and for expression evaluation.
 * @param {Object} [props.scope={}] - Additional variables available in expression evaluation. Merged with `data` as `scope.data`.
 * @returns {React.ReactNode[]} The rendered components as specified by the schema.
 *
 * @example
 * <SchemaRenderer
 *   schema={{
 *     fields: [
 *       {
 *         "x-component": "Input",
 *         "bind": "username",
 *         "x-props": { placeholder: "'Enter username'" }
 *       }
 *     ]
 *   }}
 *   data={{ username: "alice" }}
 * />
 */

export const SchemaRenderer = ({ schema, data, scope = {} }) => {
  const mergedScope = { ...scope, data }; // auto map "data.key"

  const renderField = (field, key) => {
    const Component = HSRegistry[field['x-component']];

    if (!Component) return null;

    // Visibility logic
    if (field['x-visible'] !== undefined) {
      const visible = evalExpression(field['x-visible'], mergedScope);
      if (!visible) return null;
    }

    // Bind data
    const value = field.bind && mergedScope.data ? mergedScope.data[field.bind] : undefined;

    // Evaluate props
    const xProps = {};
    if (field['x-props']) {
      Object.entries(field['x-props']).forEach(([k, v]) => {
        xProps[k] = evalExpression(v, mergedScope);
      });
    }

    return <Component key={key} value={value} {...xProps} meta={field['x-meta'] || {}} />;
  };

  const fields = schema.fields || [];

  return fields.map((f, i) => renderField(f, i));
};

SchemaRenderer.propTypes = {
  schema: PropTypes.object.isRequired,
  data: PropTypes.any,
  scope: PropTypes.object,
};
